import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdClose } from "react-icons/io";
import AddCategoryInput from "../Categories/AddCategoryInput";
import { Categories, Chef } from "../../types/globalTypes";
import EditCategoryInput from "../Categories/EditCategoryInput";
import { MdKeyboardArrowDown, MdOutlineDelete } from "react-icons/md";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";
import { BiSolidCategory } from "react-icons/bi";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import ChefsSelectInput from "./ChefsSelectInput";

export default function AddCategoryButton({
  allCategories,
  setNeedRefetch,
  allChefs,
}: {
  allCategories: Categories;
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  allChefs: Chef[];
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  useLockBodyScroll(isModalOpen);
  const [shouldReset, setShouldReset] = useState(false);

  const formik = useFormik({
    initialValues: {
      categories: allCategories,
      nameAR: "",
      nameEN: "",
      chefs: [],
    },
    validationSchema: Yup.object({
      categories: Yup.array().of(
        Yup.object({
          nameAR: Yup.string(),
          nameEN: Yup.string().test(
            "at-least-one-lang-one",
            t("addCategoryButton.atLeastOneNameRequired") as string,
            function (value) {
              const { nameAR } = this.parent;
              return Boolean(value?.trim() || nameAR?.trim());
            },
          ),
        }),
      ),
      nameAR: Yup.string(),
      nameEN: Yup.string().test(
        "at-least-one-lang-two",
        t("addCategoryButton.atLeastOneNameRequired") as string,
        function (value) {
          if (!this.parent.nameAR?.trim() && !this.parent.nameEN?.trim())
            return true;
          const { nameAR } = this.parent;
          return Boolean(value?.trim() || nameAR?.trim());
        },
      ),
    }),
    onSubmit: (values) => {
      const { categories } = values;
      const url = `${BASE_URL}/api/dashboard/categories/aggregate?locale=${lang}`;

      const NewCategories =
        categories && categories.length > 0 ? categories : [];

      const addCategory = async () => {
        const loadingToast = toast.loading(
          t("addCategoryButton.editing") as string,
        );
        try {
          const response = await axios.post(url, NewCategories, {
            headers: {
              Authorization: token,
            },
          });
          if (response.data.success === false) {
            toast.error(response.data.error);
            return;
          }
          toast.success(t("addCategoryButton.editSuccess") as string);
          setNeedRefetch(true);
        } catch (error: unknown) {
          if (axios.isAxiosError(error)) {
            toast.error(error.response?.data.error);
          } else {
            console.error(error);
            toast.error(t("addCategoryButton.unexpectedError") as string);
          }
        } finally {
          toast.dismiss(loadingToast);
        }
      };
      addCategory();
    },
  });

  const closeModal = useCallback(() => {
    formik.resetForm();
    setIsModalOpen(false);
  }, [formik]);

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        closeModal();
      }
    },
    [closeModal],
  );

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleOutsideClick, isModalOpen]);

  const handleDeleteCategory = async (_id: string) => {
    const isWarningMessageAccept = window.confirm(
      t("addCategoryButton.deleteConfirmation") as string,
    );

    if (isWarningMessageAccept) {
      formik.setFieldValue(
        "categories",
        formik.values.categories.filter((category) => category._id !== _id),
      );
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="flex items-center justify-center gap-2 rounded-full border border-sidebar bg-white px-3 py-2 text-sm font-semibold text-sidebar md:gap-2"
      >
        <BiSolidCategory size={22} />
        <span> {t("addCategoryButton.manageCategories")}</span>
        <MdKeyboardArrowDown size={19} />
      </button>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            ref={modalRef}
            className="flex w-[95%] max-w-3xl flex-col gap-7 rounded-lg bg-white px-2 py-3 shadow-lg sm:px-4 sm:py-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm font-semibold sm:text-base md:text-2xl">
                <h2 className="text-lg font-semibold">
                  {t("addCategoryButton.manageCategories")}
                </h2>
              </div>
              <button
                onClick={closeModal}
                className="mb-3 text-gray-600 hover:text-gray-800"
              >
                <IoMdClose size={24} className="text-red-600" />
              </button>
            </div>

            {/* start body */}
            <form
              id="manage-categories-form"
              onSubmit={formik.handleSubmit}
              className="thin-scrollbar max-h-[55dvh] space-y-4 overflow-y-auto px-1"
            >
              {/* User Categories */}
              <div className="flex flex-col gap-4 rounded-lg border border-gray-200 p-5">
                <label className="text-lg font-semibold">
                  {t("addCategoryButton.categories")}
                </label>
                {!formik.values.categories.length && (
                  <p className="text-sm text-gray-500">
                    {t("addCategoryButton.noCategories")}
                  </p>
                )}

                {formik.values.categories.map((category, i) => (
                  <div
                    key={`parent-${i}`}
                    className="flex flex-col gap-3 rounded-md border border-gray-300 p-3"
                  >
                    <div className="flex items-center gap-2" key={category._id}>
                      <EditCategoryInput
                        key={i}
                        EditCategoryInputs={{
                          ar: category.nameAR,
                          en: category.nameEN,
                        }}
                        onChange={(newValues) => {
                          formik.setFieldValue(
                            `categories[${i}].nameAR`,
                            newValues.ar,
                          );
                          formik.setFieldValue(
                            `categories[${i}].nameEN`,
                            newValues.en,
                          );
                        }}
                      />
                      <button
                        onClick={() => handleDeleteCategory(category._id)}
                        type="button"
                        key={`delete-${i}`}
                        className="rounded bg-red-500 px-4 py-2 font-bold text-white"
                      >
                        <MdOutlineDelete size={20} />
                      </button>
                    </div>

                    {formik.errors.categories && formik.touched.categories && (
                      <div className="flex flex-col gap-1 text-sm text-red-500">
                        {/* Type assertion to ensure you're dealing with an object */}
                        {formik.errors.categories[i] &&
                          typeof formik.errors.categories[i] !== "string" && (
                            <>
                              <span key={`err${Math.random()}`}>
                                {formik.errors.categories[i]?.nameAR}
                              </span>
                              <span key={`err${Math.random()}`}>
                                {formik.errors.categories[i]?.nameEN}
                              </span>
                            </>
                          )}
                      </div>
                    )}

                    {/* chef select input */}
                    <div className="flex items-center justify-between gap-2">
                      <label
                        className="w-full font-semibold"
                        htmlFor={`assignedChef-${i}`}
                      >
                        {t("addCategoryButton.assign_chef")} :
                      </label>

                      <ChefsSelectInput 
                      allChefs={allChefs} 
                      onChefChange={(value) =>  formik.setFieldValue(
                            `categories[${i}].chefs`,
                            value,
                          )} 
                      placeholder={t("chefsInput.select_chef")}  
                      userPrevSelectedChefs={formik.values.categories[i].chefs || []}
                    />

                    </div>
                  </div>
                ))}
              </div>

              {/* Add new category */}
              <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-5">
                <label className="text-lg font-semibold">
                  {t("addCategoryButton.addNewCategory")}
                </label>

                <div className="flex flex-col gap-3 rounded-md border border-gray-300 p-3">
                  <div className="flex items-center gap-2">
                    <AddCategoryInput
                      key={"addInputField"}
                      AddCategoryInputs={{
                        ar: formik.values.nameAR,
                        en: formik.values.nameEN,
                      }}
                      onChange={(newValues) => {
                        // Update Formik field values
                        formik.setFieldValue("nameAR", newValues.ar);
                        formik.setFieldTouched("nameAR", true); // Mark the field as touched
                        formik.setFieldValue("nameEN", newValues.en);
                        formik.setFieldTouched("nameEN", true); // Mark the field as touched
                      }}
                    />
                    <button
                      onClick={() => {
                        // Create a new category object
                        const newCategory = {
                          nameAR: formik.values.nameAR,
                          nameEN: formik.values.nameEN,
                          chefs: formik.values.chefs || [],
                        };

                        // Update the categories array
                        formik.setFieldValue("categories", [
                          ...formik.values.categories,
                          newCategory,
                        ]);

                        // Optionally clear the form fields after adding
                        formik.setFieldValue("nameAR", "");
                        formik.setFieldValue("nameEN", "");
                        formik.setFieldValue("chefs", "");
                        formik.setFieldTouched("nameAR", false); // Mark the field as not touched
                        formik.setFieldTouched("nameEN", false);
                        formik.setFieldTouched("chefs", false);

                        setShouldReset(true);
                        return () => setShouldReset(false);
                      }}
                      type="button"
                      disabled={!formik.values.nameAR && !formik.values.nameEN}
                      className={`min w-[130px] rounded bg-sidebar px-2 py-3 text-sm font-bold text-skyblue disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600`}
                    >
                      {t("addCategoryButton.addCategory")}
                    </button>
                  </div>

                  {formik.errors.nameAR && formik.touched.nameAR && (
                    <div className="text-sm text-red-600">
                      {formik.errors.nameAR}
                    </div>
                  )}
                  {formik.errors.nameEN && formik.touched.nameEN && (
                    <div className="text-sm text-red-600">
                      {formik.errors.nameEN}
                    </div>
                  )}

                  {/* chef select input */}
                  <div className="flex items-center justify-between gap-2">
                    <label
                      className="w-full font-semibold"
                      htmlFor={`assignedChef-create`}
                    >
                      {t("addCategoryButton.assign_chef")} :
                    </label>

                    <ChefsSelectInput 
                    allChefs={allChefs} 
                    onChefChange={(value) => formik.setFieldValue("chefs", value)} 
                    placeholder={t("chefsInput.select_chef")}  
                    userPrevSelectedChefs={[]}
                    shouldReset={shouldReset}
                    />
                    
                  </div>
                </div>
              </div>
            </form>
            {/* end body */}

            <div className="mt-2 flex justify-end">
              <button
                form="manage-categories-form"
                type="submit"
                className="w-fit rounded-md bg-sidebar px-4 py-2 text-sm font-semibold text-skyblue md:text-base"
              >
                {t("addCategoryButton.saveAllChanges")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
